import { Card, CardActions, CardContent, Slide, Theme, Typography } from '@material-ui/core';
import React, { MutableRefObject, ReactElement, useEffect, useState } from 'react';
import ivaCardStyles from './AlmeIvaResponse.styles';
import { withStyles, WithStyles, createStyles, makeStyles } from '@material-ui/core/styles';
import {
    getAgentResponse,
    onTriggerAutoNavUrl,
    paintNavUrl,
    paintSection,
    paintSectionHeader,
} from '../AlmeResponseUtils';
import AlmeCtaButton, { CtaResponseAction } from '../../ResponseActions/AlmeCtaButton/AlmeCtaButton';
import AlmeSurvey, { FireSurveyAppEvent } from '../../ResponseActions/AlmeSurvey/AlmeSurvey';
import { DisplayLink } from '@alme/types/response/DisplayLink';
import { FocusComponentProps } from '../../../models/ComponentProps';
import { AlmeMarkdownResponseNext, AlmeResponseClasses } from '../AlmeResponse';
import { AlmeIvaResponseConfig } from './AlmeIvaResponseConfig';
import { NavUrl } from '@alme/types/response/NavUrl';
import ChangeTenantConfirmation from "../../ResponseActions/ChangeTenantConfirmation/ChangeTenantConfirmation";

const useStyles = makeStyles(({ spacing }: Theme) =>
    createStyles({
        userCardContainer: {
            display: 'flex',
            flexFlow: 'row nowrap',
            justifyContent: 'flex-end',
        },
        displayLink: {
            textAlign: 'left',
            justifyContent: 'normal',
            marginTop: '10px',
            "&.Mui-focusVisible": { outline: "2px solid black" },
        },
        navUrl: {
            marginTop: spacing(2),
        },
        sectionHeader: {
            paddingTop: spacing(2),
        },
    }),
);

export type AlmeIvaCardClasses = Record<"displayLink" | "navUrl", string>;

interface AlmeIvaResponseProps extends WithStyles<typeof ivaCardStyles> {
    almeConfig: AlmeIvaResponseConfig;
    response: AlmeMarkdownResponseNext;
    responseRevision: string;
    isLastResponse: boolean;
    entryId: string;
    agentResponseRef: React.RefObject<HTMLDivElement>;
    onScroll: () => void;
    onCtaClick: (action: CtaResponseAction, entryId: string) => Promise<void>;
    setIsDialogOpen: (isOpen: boolean) => void;
    onDisplayLinkClick: (link: DisplayLink | NavUrl) => void;
    onSurveySubmitClick: FireSurveyAppEvent;
    ivaIcon?: ReactElement;
    isDialogOpen?: boolean;
    focusProps?: FocusComponentProps;
    scrollToResponse?: MutableRefObject<boolean>;
}

const AlmeIvaResponse: React.FC<AlmeIvaResponseProps> = (props: AlmeIvaResponseProps) => {
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const classes: AlmeResponseClasses = useStyles();
    const responseText = getAgentResponse(props.response, `${props.classes.ivaCardBodyText}`, `${props.classes.inlineButton}`, props.onDisplayLinkClick);
    const hasIvaResponse = !!(responseText || props.response.navUrl || (props.response.displayLinkCollection?.Sections.length));
    const isSessionTimeout = props.response.extensionData["isSessionTimeoutResponse"]
        && props.response.extensionData["isSessionTimeoutResponse"] === true;

    const responseAccessibilityLabel = <span hidden={true} aria-hidden="false">Message from bot. </span>;

    const shouldDisableOnClick = (props.response.navUrl && props.response.navUrl?.Metadata?.DisableOnClick) ? true : undefined;
    const onLinkClick = () => {
            props.onDisplayLinkClick(props.response.navUrl as DisplayLink | NavUrl);
            if(shouldDisableOnClick) {
                setDisableButton(true);
            }
        };


    useEffect(() => {
        if (props.response.navUrl && props.almeConfig.ivaResponse.shouldAutoNav) {
            onTriggerAutoNavUrl(props.response.navUrl);
        }
        // Empty dependency-list so autoNav only happens once:
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let ivaIcon = props.ivaIcon;
    if (props.ivaIcon) {
        ivaIcon = React.cloneElement(
            props.ivaIcon,
            {
                alt:"",
            },
        );
    }

    if (!hasIvaResponse) {
        return null;
    }
    else if (isSessionTimeout) {
        return (
            <>
                {responseAccessibilityLabel}
                <Typography>{responseText}</Typography>
            </>
        );
    }
    else {
        return (
            <Slide direction='up' in onEntered={props.onScroll}>
                <div className={props.classes.ivaCardContainer} id={`alme-iva-card-${props.responseRevision}`}>
                    {ivaIcon}
                    <Card
                        className={props.classes.ivaCard}
                        raised={props.isLastResponse}
                        key={`${props.entryId}-alme`}
                        ref={props.agentResponseRef}
                        {...props.focusProps}
                    >
                        <CardContent className={`${props.classes.ivaCardContent}`}>
                            {responseAccessibilityLabel}
                            {responseText}
                            <ChangeTenantConfirmation
                                response={props.response}
                                responseId={props.responseRevision}
                                almeConfig={props.almeConfig.ivaResponse}
                                focusProps={props.focusProps}
                            />
                            {paintNavUrl(props.entryId, props.response.navUrl, props.responseRevision, classes, props.almeConfig, onLinkClick, props.focusProps, undefined, disableButton)}
                            <AlmeCtaButton
                                response={props.response}
                                responseId={props.responseRevision}
                                almeConfig={props.almeConfig.ivaResponse}
                                focusProps={props.focusProps}
                                onCtaClick={props.onCtaClick}
                            />
                            {props.response.displayLinkCollection && !!props.response.displayLinkCollection.Sections.length &&
                                <> {paintSectionHeader(props.response.displayLinkCollection.Sections, classes.sectionHeader)}
                                    {props.response.displayLinkCollection.Sections.reduce((elements: ReactElement[], section): ReactElement[] => {
                                        return elements.concat(paintSection(props.entryId, section, props.onDisplayLinkClick, classes, props.almeConfig, props.focusProps));
                                    }, [] as ReactElement[])} </>
                            }
                        </CardContent>
                        <CardActions className={props.classes.ivaCardActions} disableSpacing={true}>
                            <AlmeSurvey
                                responseId={props.responseRevision}
                                almeConfig={props.almeConfig.ivaResponse}
                                setIsDialogOpen={props.setIsDialogOpen}
                                response={props.response}
                                onSubmitClick={props.onSurveySubmitClick}
                                isLastResponse={props.isLastResponse}
                                focusProps={props.focusProps}
                                isDialogOpen={props.isDialogOpen}
                                scrollToResponse={props.scrollToResponse}
                            />
                        </CardActions>
                    </Card>
                </div>
            </Slide>
        );
    }
};

export default withStyles(ivaCardStyles)(AlmeIvaResponse);
